import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Upload, LogOut, Key, Download, Mail, FileText, 
  Trash2, Edit, X, Save, Users, BarChart3, Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pdfs');
  
  // Auth state
  const [token, setToken] = useState(null);
  const [username, setUsername] = useState('');
  
  // PDF state
  const [pdfs, setPdfs] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPdf, setEditingPdf] = useState(null);
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    category: 'Money',
    file: null
  });
  
  // Email state
  const [subscribers, setSubscribers] = useState([]);
  const [subscriberToDelete, setSubscriberToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Analytics state
  const [analytics, setAnalytics] = useState({});
  
  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    const storedToken = localStorage.getItem('admin_token');
    const storedUsername = localStorage.getItem('admin_username');
    
    if (!storedToken) {
      navigate('/admin');
      return;
    }
    
    setToken(storedToken);
    setUsername(storedUsername);
  }, [navigate]);

  useEffect(() => {
    if (token) {
      fetchPdfs();
      fetchSubscribers();
      fetchAnalytics();
    }
  }, [token]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const fetchPdfs = async () => {
    try {
      const response = await axios.get(`${API}/pdfs`);
      setPdfs(response.data);
    } catch (error) {
      console.error('Error fetching PDFs:', error);
      toast.error('Failed to fetch PDFs');
    }
  };

  const fetchSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/subscribers`, getAuthHeaders());
      setSubscribers(response.data);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      toast.error('Failed to fetch subscribers');
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/downloads`, getAuthHeaders());
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to fetch analytics');
    }
  };

  const handleUploadPdf = async (e) => {
    e.preventDefault();
    
    if (!uploadData.file) {
      toast.error('Please select a PDF file');
      return;
    }

    const formData = new FormData();
    formData.append('title', uploadData.title);
    formData.append('description', uploadData.description);
    formData.append('category', uploadData.category);
    formData.append('file', uploadData.file);

    try {
      await axios.post(`${API}/pdfs`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('PDF uploaded successfully!');
      setShowUploadModal(false);
      setUploadData({ title: '', description: '', category: 'Money', file: null });
      fetchPdfs();
    } catch (error) {
      console.error('Error uploading PDF:', error);
      toast.error('Failed to upload PDF');
    }
  };

  const handleUpdatePdf = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(
        `${API}/pdfs/${editingPdf.id}`,
        {
          title: editingPdf.title,
          description: editingPdf.description,
          category: editingPdf.category
        },
        getAuthHeaders()
      );
      
      toast.success('PDF updated successfully!');
      setShowEditModal(false);
      setEditingPdf(null);
      fetchPdfs();
    } catch (error) {
      console.error('Error updating PDF:', error);
      toast.error('Failed to update PDF');
    }
  };

  const handleDeletePdf = async (pdfId) => {
    if (!window.confirm('Are you sure you want to delete this PDF?')) return;
    
    try {
      await axios.delete(`${API}/pdfs/${pdfId}`, getAuthHeaders());
      toast.success('PDF deleted successfully!');
      fetchPdfs();
      fetchAnalytics();
    } catch (error) {
      console.error('Error deleting PDF:', error);
      toast.error('Failed to delete PDF');
    }
  };

  const handleExportSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/subscribers/export`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'subscribers.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Subscribers exported successfully!');
    } catch (error) {
      console.error('Error exporting subscribers:', error);
      toast.error('Failed to export subscribers');
    }
  };

  const handleDeleteSubscriber = async () => {
    if (!subscriberToDelete) return;
    
    try {
      await axios.delete(`${API}/subscribers/${subscriberToDelete.id}`, getAuthHeaders());
      toast.success('Subscriber deleted successfully!');
      setShowDeleteConfirm(false);
      setSubscriberToDelete(null);
      fetchSubscribers();
    } catch (error) {
      console.error('Error deleting subscriber:', error);
      toast.error('Failed to delete subscriber');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    try {
      await axios.post(
        `${API}/auth/change-password`,
        {
          old_password: passwordData.old_password,
          new_password: passwordData.new_password
        },
        getAuthHeaders()
      );
      
      toast.success('Password changed successfully!');
      setShowPasswordModal(false);
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  if (!token) return null;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_4a1e8a2c-d558-43d3-a645-acf842c0f069/artifacts/jxrv1t5f_500px-invigoreight-pic.png"
              alt="InvigorEight Logo"
              className="h-10"
            />
            <div>
              <h1 className="text-xl font-semibold text-stone-900" data-testid="dashboard-title">Admin Dashboard</h1>
              <p className="text-sm text-stone-500">Welcome, {username}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPasswordModal(true)}
              data-testid="change-password-button"
            >
              <Key className="h-4 w-4 mr-2" />
              Change Password
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-3" data-testid="dashboard-tabs">
            <TabsTrigger value="pdfs" data-testid="tab-pdfs">
              <FileText className="h-4 w-4 mr-2" />
              PDFs
            </TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="tab-subscribers">
              <Users className="h-4 w-4 mr-2" />
              Emails
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* PDFs Tab */}
          <TabsContent value="pdfs" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-stone-900">Manage PDFs</h2>
                <p className="text-stone-600">Upload and manage your free resources</p>
              </div>
              <Button
                onClick={() => setShowUploadModal(true)}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="upload-pdf-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Upload PDF
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pdfs.map((pdf) => (
                <Card key={pdf.id} data-testid={`pdf-card-${pdf.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <span className="inline-block px-2 py-1 bg-red-50 text-red-600 text-xs rounded mb-2">
                          {pdf.category}
                        </span>
                        <CardTitle className="text-lg" data-testid={`pdf-card-title-${pdf.id}`}>{pdf.title}</CardTitle>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingPdf(pdf);
                            setShowEditModal(true);
                          }}
                          data-testid={`edit-pdf-${pdf.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePdf(pdf.id)}
                          data-testid={`delete-pdf-${pdf.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-stone-600 mb-3">{pdf.description}</p>
                    <div className="flex items-center justify-between text-xs text-stone-500">
                      <span className="flex items-center gap-1">
                        <Download className="h-3 w-3" />
                        {pdf.download_count} downloads
                      </span>
                      <span>{new Date(pdf.uploaded_at).toLocaleDateString()}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {pdfs.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="h-12 w-12 text-stone-400 mx-auto mb-4" />
                  <p className="text-stone-600">No PDFs uploaded yet</p>
                  <Button
                    onClick={() => setShowUploadModal(true)}
                    className="mt-4"
                    variant="outline"
                  >
                    Upload Your First PDF
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Subscribers Tab */}
          <TabsContent value="subscribers" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-stone-900">Email Subscribers</h2>
                <p className="text-stone-600">{subscribers.length} total subscribers</p>
              </div>
              <Button
                onClick={handleExportSubscribers}
                variant="outline"
                data-testid="export-subscribers-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-200">
                      <tr>
                        <th className="text-left px-6 py-3 text-xs font-medium text-stone-600 uppercase">Name</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-stone-600 uppercase">Email</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-stone-600 uppercase">Subscribed</th>
                        <th className="text-right px-6 py-3 text-xs font-medium text-stone-600 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-200">
                      {subscribers.map((sub) => (
                        <tr key={sub.id} className="hover:bg-stone-50" data-testid={`subscriber-row-${sub.id}`}>
                          <td className="px-6 py-4 text-sm text-stone-900">{sub.first_name}</td>
                          <td className="px-6 py-4 text-sm text-stone-600">{sub.email}</td>
                          <td className="px-6 py-4 text-sm text-stone-500">
                            {new Date(sub.subscribed_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSubscriberToDelete(sub);
                                setShowDeleteConfirm(true);
                              }}
                              data-testid={`delete-subscriber-${sub.id}`}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {subscribers.length === 0 && (
                    <div className="py-12 text-center">
                      <Mail className="h-12 w-12 text-stone-400 mx-auto mb-4" />
                      <p className="text-stone-600">No subscribers yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-stone-900">Download Analytics</h2>
              <p className="text-stone-600">Detailed tracking of PDF downloads by user</p>
            </div>

            {Object.keys(analytics).length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <BarChart3 className="h-12 w-12 text-stone-400 mx-auto mb-4" />
                  <p className="text-stone-600">No download data yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {pdfs.map((pdf) => {
                  const pdfDownloads = analytics[pdf.id] || [];
                  if (pdfDownloads.length === 0) return null;

                  return (
                    <Card key={pdf.id} data-testid={`analytics-card-${pdf.id}`}>
                      <CardHeader>
                        <CardTitle className="text-lg">{pdf.title}</CardTitle>
                        <CardDescription>
                          {pdfDownloads.length} downloads
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-stone-50">
                              <tr>
                                <th className="text-left px-4 py-2 text-xs font-medium text-stone-600">Name</th>
                                <th className="text-left px-4 py-2 text-xs font-medium text-stone-600">Email</th>
                                <th className="text-left px-4 py-2 text-xs font-medium text-stone-600">Downloaded</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                              {pdfDownloads.map((download, idx) => (
                                <tr key={idx}>
                                  <td className="px-4 py-2 text-sm text-stone-900">{download.first_name}</td>
                                  <td className="px-4 py-2 text-sm text-stone-600">{download.email}</td>
                                  <td className="px-4 py-2 text-sm text-stone-500">
                                    {new Date(download.downloaded_at).toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Upload PDF Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-lg" data-testid="upload-pdf-modal">
          <DialogHeader>
            <DialogTitle>Upload New PDF</DialogTitle>
            <DialogDescription>
              Add a new free resource for your subscribers
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleUploadPdf} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={uploadData.title}
                onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                placeholder="The InvigorEight Money Guide"
                required
                data-testid="upload-title-input"
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={uploadData.description}
                onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                placeholder="A comprehensive guide to rebuilding your financial foundation..."
                rows={3}
                required
                data-testid="upload-description-input"
              />
            </div>
            
            <div>
              <Label htmlFor="category">Category</Label>
              <Select
                value={uploadData.category}
                onValueChange={(value) => setUploadData({ ...uploadData, category: value })}
              >
                <SelectTrigger data-testid="upload-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  <SelectItem value="Money">Money</SelectItem>
                  <SelectItem value="Lifestyle">Lifestyle</SelectItem>
                  <SelectItem value="Health">Health</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="file">PDF File</Label>
              <Input
                id="file"
                type="file"
                accept=".pdf"
                onChange={(e) => setUploadData({ ...uploadData, file: e.target.files[0] })}
                required
                data-testid="upload-file-input"
              />
            </div>
            
            <div className="flex gap-2 justify-end pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowUploadModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="upload-submit-button"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload PDF
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit PDF Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-pdf-modal">
          <DialogHeader>
            <DialogTitle>Edit PDF</DialogTitle>
            <DialogDescription>
              Update PDF information (file cannot be changed)
            </DialogDescription>
          </DialogHeader>
          
          {editingPdf && (
            <form onSubmit={handleUpdatePdf} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="edit-title">Title</Label>
                <Input
                  id="edit-title"
                  value={editingPdf.title}
                  onChange={(e) => setEditingPdf({ ...editingPdf, title: e.target.value })}
                  required
                  data-testid="edit-title-input"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={editingPdf.description}
                  onChange={(e) => setEditingPdf({ ...editingPdf, description: e.target.value })}
                  rows={3}
                  required
                  data-testid="edit-description-input"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-category">Category</Label>
                <Select
                  value={editingPdf.category}
                  onValueChange={(value) => setEditingPdf({ ...editingPdf, category: value })}
                >
                  <SelectTrigger data-testid="edit-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="Money">Money</SelectItem>
                    <SelectItem value="Lifestyle">Lifestyle</SelectItem>
                    <SelectItem value="Health">Health</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-2 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="edit-submit-button"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="sm:max-w-md" data-testid="password-modal">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Update your admin password for security
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="old-password">Current Password</Label>
              <Input
                id="old-password"
                type="password"
                value={passwordData.old_password}
                onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                required
                data-testid="old-password-input"
              />
            </div>
            
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                required
                data-testid="new-password-input"
              />
            </div>
            
            <div>
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                required
                data-testid="confirm-password-input"
              />
            </div>
            
            <div className="flex gap-2 justify-end pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPasswordModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="password-submit-button"
              >
                <Key className="h-4 w-4 mr-2" />
                Change Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Subscriber Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent data-testid="delete-subscriber-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{subscriberToDelete?.first_name}</strong> ({subscriberToDelete?.email}) from your subscriber list. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => {
                setShowDeleteConfirm(false);
                setSubscriberToDelete(null);
              }}
              data-testid="cancel-delete-subscriber"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSubscriber}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-subscriber"
            >
              Delete Subscriber
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
